/**
 * The signed-in account: read it, change it, close it.
 *
 * One endpoint backs Personal, Company, Calls and Billing details, because they
 * are four groupings of one `users` row rather than four resources. Each card
 * therefore PATCHes only the fields it owns — a partial update, so two cards
 * saved in sequence cannot overwrite each other's values.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiDelete, apiGet, apiPatch } from '../client'
import type { components } from '../generated/schema'

export type Profile = components['schemas']['UserProfileOut']
export type ProfileUpdate = components['schemas']['UserProfileUpdate']
/** Closed vocabulary — the backend rejects anything else. */
export type ReportLanguage = NonNullable<ProfileUpdate['report_language']>

export const profileKey = ['profile'] as const

export function useProfile() {
  return useQuery<Profile>({
    queryKey: profileKey,
    queryFn: () => apiGet<Profile>('/users/me'),
    staleTime: 30_000,
  })
}

export function useUpdateProfile() {
  const client = useQueryClient()
  return useMutation({
    // Writes are never retried (docs/16 section 8.3).
    retry: false,
    mutationFn: (patch: ProfileUpdate) => apiPatch<Profile>('/users/me', patch),
    // The server returns the saved row; writing that into the cache is caching,
    // not predicting. A field the backend normalised or truncated shows the
    // stored value rather than what was typed.
    onSuccess: (saved) => client.setQueryData(profileKey, saved),
  })
}

export function useDeleteAccount() {
  return useMutation({
    retry: false,
    mutationFn: () => apiDelete('/users/me'),
  })
}
